import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import gotoUnits from "../assets/images/gotoUnits.png";
import useFleetStore from "../utils/store";
import { convertCelsiusToFahrenheit, getConnectionTypeLabel } from "../utils/common_utils";
import { getAllUnit, deleteUnit } from "../lib/api";
import { SearchContext } from "../lib/analyticsEvents";
import { useFleetStatusCheck } from "../hooks/useFleetStatusCheck";
import { CURRENT_LIMIT_DISPLAY_VALUES, OP_MODE_DISPLAY_VALUES } from "../utils/constants";
import {
  FIRST_PAGE,
  ORDER_OPTIONS,
  ZONE_UNIT_FILTER_SECTIONS,
  ZONE_UNIT_LIST_QUERY,
  ZONE_UNIT_SORT_OPTIONS,
  emptyFilters,
  hasActiveFilters,
  lastPageForCount,
  readUnitListQuery,
  toZoneUnitApiFilters,
  writeUnitListQuery,
} from "../utils/unitListQuery";
import { unitListReturnState } from "../utils/unitListReturn";
import ViewUnitCardComponent from "../components/ViewUnitCardComponent";
import ConfirmModal from "../components/Common/ConfirmModal";
import ToastMessage from "../components/Common/ToastMessage";
import LoadingModal from "../components/Common/LoadingModal";
import SearchComponent from "../components/Common/SearchComponent";
import SortByDropdown from "../components/Common/SortByFilter";
import GridListViewComponent from "../components/Common/GridListViewComponent";
import UnitsListViewComponent from "../components/Common/UnitsListViewComponent";
import gotoUnitsInverse from "../assets/images/gotoUnitsInverse.png";
import FilterDropDown from "../components/Common/FilterDropDown";
import Pagination from "../components/Common/Pagination";
import BulkUnitSelectionContainer from "../components/BulkUnitSelectionContainer";
import GenerateReportModal from "../components/Common/GenerateReportModal";
import Button from "../components/Common/Button";

const DEFAULT_PAGE_SIZE = 25;
const GRID_VIEW_STORAGE_KEY = "unitsViewMode";
const CSV_FETCH_PAGE_SIZE = 100;

const escapeCsvValue = (value) => {
  const stringValue = String(value ?? "");
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const escapeUserCsvValue = (value) => {
  const stringValue = String(value ?? "");
  const formulaGuarded = /^[=+\-@\t\r]/.test(stringValue) ? `'${stringValue}` : stringValue;
  return escapeCsvValue(formulaGuarded);
};

export default function Units() {
  const selectedFleet = useFleetStore((state) => state.selectedFleet);
  const getStoredGridView = () => {
    if (window.innerWidth < 1025) return true;
    try {
      const stored = localStorage.getItem(GRID_VIEW_STORAGE_KEY);
      return stored ? stored === "grid" : true;
    } catch (error) {
      return true;
    }
  };
  const allPodData = useFleetStore((state) => state.allPodData);
  const [unitsByFleetUnId, setUnitsByFleetUnId] = useState([]);
  const [unitsFetching, setUnitsFetching] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [activeDropdownId, setActiveDropdownId] = useState(null);
  const [showDeleteModel, setShowDeleteModel] = useState(false);
  const [gridView, setGridView] = useState(() => getStoredGridView());
  const [hasMoreUnits, setHasMoreUnits] = useState(true);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE); // TODO: implement pagination properly using page number, not a large page size
  const loaderRef = useRef(null);
  const [totalCount, setTotalCount] = useState(0);

  const [isGenerateReportModalOpen, setIsGenerateReportModalOpen] = useState(false);
  const [reportPrefillData, setReportPrefillData] = useState(null);
  const [isDownloadingUnitsCsv, setIsDownloadingUnitsCsv] = useState(false);

  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  const navigate = useNavigate();

  const location = useLocation();

  const showToast = useCallback((message, type = "success") => {
    setToast({ show: true, message, type });
  }, [setToast]);

  const { page, search: searchUnitText, sortBy, sortDirection, filters } = useMemo(
    () => readUnitListQuery(location.search, ZONE_UNIT_LIST_QUERY),
    [location.search]
  );
  const currentPage = page - FIRST_PAGE;

  const updateQuery = useCallback((changes, { replace = false } = {}) => {
    const current = readUnitListQuery(location.search, ZONE_UNIT_LIST_QUERY);
    navigate({
      pathname: location.pathname,
      search: writeUnitListQuery(location.search, ZONE_UNIT_LIST_QUERY, { ...current, ...changes }),
    }, { replace });
  }, [location.pathname, location.search, navigate]);

  const isFiltersApplied = hasActiveFilters(filters);

  const handleSortReset = () => {
    updateQuery({ sortBy: "", sortDirection: "" });
  };

  const handleSortApply = ({ order, sortBy: nextSortBy }) => {
    updateQuery({ sortBy: nextSortBy, sortDirection: order });
  };

  const resetFilters = () => {
    updateQuery({ filters: emptyFilters(ZONE_UNIT_FILTER_SECTIONS), page: FIRST_PAGE });
  }

  const applyFilters = (nextFilters) => {
    updateQuery({ filters: nextFilters, page: FIRST_PAGE });
  }


  const apiFilters = useMemo(() => toZoneUnitApiFilters(filters), [filters]);

  const fetchUnits = useCallback(
    async (showloader = false, pageSizeOverride) => {
      const requestedPageSize = pageSizeOverride ? pageSizeOverride : pageSize;
      if (showloader) {
        setUnitsFetching(true);
      }
      try {
        const response = await getAllUnit({
          pageNumber: page,
          pageSize: requestedPageSize,
          unid: selectedFleet?.unid,
          search: searchUnitText,
          sortBy,
          sortDirection,
          ...apiFilters,
        });

        const newUnits = response?.data?.data?.units || [];
        const totalCount = response?.data?.data?.count || 0;
        const transformedUnits = newUnits.map((unit) => {
          const status = unit?.status || {};
          return {
            ...unit,
            status: {
              ...status,
              indoorTempF:
                status.indoorTempC != null
                ? parseFloat(convertCelsiusToFahrenheit(status.indoorTempC))
                : null,
              coolTargetTempF:
                status.coolTargetTempC != null
                  ? parseFloat(convertCelsiusToFahrenheit(status.coolTargetTempC))
                  : null,
              heatTargetTempF:
                status.heatTargetTempC != null
                  ? parseFloat(convertCelsiusToFahrenheit(status.heatTargetTempC))
                  : null,
            },
          };
        });
        setUnitsByFleetUnId(transformedUnits);
        setHasMoreUnits(transformedUnits.length < totalCount);
        setTotalCount(totalCount);

        const lastPage = lastPageForCount(totalCount, requestedPageSize);
        if (page > lastPage) {
          updateQuery({ page: lastPage }, { replace: true });
        }
      } catch (error) {
        console.error("Error fetching units:", error);
        setUnitsByFleetUnId([]);
      } finally {

        setUnitsFetching(false);

      }
    },
    [selectedFleet?.unid, searchUnitText, apiFilters, sortBy, sortDirection, page, pageSize, updateQuery]
  );

  useEffect(() => {
    fetchUnits(true, pageSize);
  }, [fetchUnits, pageSize]);

  const fetchAllUnitsForCsv = useCallback(async () => {
    const allUnits = [];
    let pageNumber = 1;
    let total = Infinity;

    while (allUnits.length < total) {
      const response = await getAllUnit({
        pageNumber,
        pageSize: CSV_FETCH_PAGE_SIZE,
        unid: selectedFleet?.unid,
        search: searchUnitText,
        sortBy,
        sortDirection,
        ...apiFilters,
      });

      const units = response?.data?.data?.units || [];
      total = response?.data?.data?.count || 0;

      if (units.length === 0) {
        break;
      }

      allUnits.push(...units);
      pageNumber += 1;
    }

    return allUnits;
  }, [selectedFleet?.unid, searchUnitText, sortBy, sortDirection, apiFilters]);

  const handleDownloadUnitsCsv = useCallback(async () => {
    if (isDownloadingUnitsCsv) return;
    setIsDownloadingUnitsCsv(true);
    try {
      const units = await fetchAllUnitsForCsv();
      const generatedAt = new Date().toLocaleString();

      const headers = [
        "Unit Name",
        "Serial Number",
        "Property",
        "Building",
        "Unit",
        "Floor",
        "Room",
        "Connected",
        "Connection Type",
        "Power",
        "Mode",
        "Indoor Temperature (°F)",
        "Heat Setpoint (°F)",
        "Cool Setpoint (°F)",
        "Control Source",
        "Schedule Source",
        "Current Limit",
        "Timestamp Generated",
      ];

      const rows = units.map((unit) => {
        const metadata = unit?.metadata || {};
        const status = unit?.status || {};
        const connected = status.connected === true ? "Connected" : "Not Connected";
        const connectionType =
          status.connected === true ? getConnectionTypeLabel(status.connectionType) : "";
        const power =
          status.running === true
            ? "Power On"
            : status.running === false
              ? "Power Off"
              : "";
        const mode = OP_MODE_DISPLAY_VALUES[status.mode] || "";
        const indoorTempF =
          status.indoorTempC != null ? convertCelsiusToFahrenheit(status.indoorTempC) : "";
        const heatTargetTempF =
          status.heatTargetTempC != null ? convertCelsiusToFahrenheit(status.heatTargetTempC) : "";
        const coolTargetTempF =
          status.coolTargetTempC != null ? convertCelsiusToFahrenheit(status.coolTargetTempC) : "";

        return [
          escapeUserCsvValue(metadata.name),
          escapeCsvValue(unit.serialNumber || ""),
          escapeUserCsvValue(metadata.propertyName),
          escapeUserCsvValue(metadata.buildingName),
          escapeUserCsvValue(metadata.unit),
          escapeUserCsvValue(metadata.floor),
          escapeUserCsvValue(metadata.room),
          escapeCsvValue(connected),
          escapeUserCsvValue(connectionType),
          escapeCsvValue(power),
          escapeCsvValue(mode),
          escapeCsvValue(indoorTempF),
          escapeCsvValue(heatTargetTempF),
          escapeCsvValue(coolTargetTempF),
          escapeCsvValue(status.controlSource || ""),
          escapeCsvValue(status.scheduleSource || ""),
          escapeCsvValue(CURRENT_LIMIT_DISPLAY_VALUES[status.currentLimit] || ""),
          escapeCsvValue(generatedAt),
        ];
      });

      const csvContent = [headers.map(escapeCsvValue), ...rows]
        .map((row) => row.join(","))
        .join("\n");

      const fleetNamePart = (selectedFleet?.name || "zone")
        .trim()
        .replace(/[^a-z0-9]+/gi, "-")
        .replace(/^-+|-+$/g, "")
        .toLowerCase();
      const timestampPart = generatedAt
        .replace(/[^a-z0-9]+/gi, "-")
        .replace(/^-+|-+$/g, "");

      const downloadLink = document.createElement("a");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const objectUrl = window.URL.createObjectURL(blob);

      downloadLink.href = objectUrl;
      downloadLink.download = `${fleetNamePart || "zone"}-units-${timestampPart}.csv`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Error downloading units CSV:", error);
      showToast("Failed to download units CSV", "failed");
    } finally {
      setIsDownloadingUnitsCsv(false);
    }
  }, [isDownloadingUnitsCsv, fetchAllUnitsForCsv, selectedFleet?.name, showToast]);

  const handleNavigateToUnits = () => {
    navigate("/manageUnits");
  };

  const handleGenerateReport = (unit) => {
    const targetUnit = unit || selectedUnit;
    setActiveDropdownId(null);
    setReportPrefillData(
      targetUnit?.serialNumber
        ? { fleetUnid: selectedFleet?.unid, unitSerialNumber: targetUnit.serialNumber }
        : null
    );
    setIsGenerateReportModalOpen(true);
  };

  const handleEditUnit = (unit) => {
    setActiveDropdownId(null);
    navigate(
      `/EditUnit?unid=${selectedFleet?.unid}&serialNumber=${unit?.serialNumber}`,
      { state: unitListReturnState("units", location.search) }
    );
  };

  const handleRemoveUnit = () => {
    setShowDeleteModel(true);
    setActiveDropdownId(null);
  };

  const makeHandleRemoveConfirmCallback = (removeUnitsFromSelectedUnitsForBulkAction) => async () => {
    const payload = {
      unid: selectedFleet.unid,
      serialNumber: selectedUnit.serialNumber,
    };
    try {
      setShowDeleteModel(false);
      await deleteUnit(payload);
      setHasMoreUnits(true)
      removeUnitsFromSelectedUnitsForBulkAction(selectedUnit);
      await fetchUnits(true, DEFAULT_PAGE_SIZE);
      showToast("Unit removed from zone successfully");
    } catch (error) {
      showToast("Failed to remove unit", "failed");
    }
  };

  const handleCancel = () => {
    setShowDeleteModel(false);
    setSelectedUnit(null);
  };

  useEffect(() => {
    if (location.state?.toast) {
      setToast({
        show: true,
        message: location.state.toast.message,
        type: location.state.toast.type,
      });
      navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, location.search, navigate]);

  useFleetStatusCheck({
    unid: selectedFleet?.unid,
    isActive: location.pathname === "/units",
    onComplete: () => fetchUnits(false, pageSize),
  });


  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1025) {
        setGridView(true);
        return;
      }
      setGridView(getStoredGridView());
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (window.innerWidth < 1025) return;
    try {
      localStorage.setItem(GRID_VIEW_STORAGE_KEY, gridView ? "grid" : "list");
    } catch (error) {
      // Ignore localStorage write failures (e.g. privacy mode).
    }
  }, [gridView]);

  const pageCount = Math.ceil(totalCount / DEFAULT_PAGE_SIZE);

  const handlePageClick = ({ selected }) => {
    updateQuery({ page: selected + FIRST_PAGE });
  };

  const isInitialUnitsLoading = unitsFetching && unitsByFleetUnId?.length === 0;
  const isEmptyUnitsResult = !unitsFetching && unitsByFleetUnId?.length === 0;

  const handleBulkZoneChangeOrRemoval = useCallback(() => {
    if (page === FIRST_PAGE) {
      fetchUnits(true, DEFAULT_PAGE_SIZE);
    } else {
      updateQuery({ page: FIRST_PAGE });
    }
  }, [page, fetchUnits, updateQuery]);

  return (
    <BulkUnitSelectionContainer
      onBulkChangeZone={handleBulkZoneChangeOrRemoval}
      onBulkRemoveFromZone={handleBulkZoneChangeOrRemoval}
      setLoading={setUnitsFetching}
      showToast={showToast}
    >
      {
        ({bulkActionHeader, selectedUnitsForBulkAction, addUnitsToSelectedUnitsForBulkAction, removeUnitsFromSelectedUnitsForBulkAction}) => <>
          {unitsFetching && <LoadingModal />}

          {unitsByFleetUnId?.length === 0 && searchUnitText === "" && !unitsFetching && !isFiltersApplied ? (
            <div className="w-full sm:w-3/4 grow flex flex-col justify-center md:w-96 mx-auto" data-testid="units-empty-state">
              <div className="w-44 h-40 mx-auto">
                <img
                  src={gotoUnits}
                  alt="go to units"
                  className="size-full h-auto mix-blend-multiply dark:hidden block"
                />
                <img
                  src={gotoUnitsInverse}
                  alt="go to units"
                  className="size-full h-auto hidden dark:block"
                />
              </div>
              <div>
                <h3 className="subtitle dark:text-white text-center text-blue font-semibold tracking-[0.036px] text-xl leading-5 lg:text-2xl lg:leading-6 mt-4">
                  You’re All Set—Almost
                </h3>
                <p className="text-dark-grey dark:text-white text-lg leading-6 font-normal tracking-[0.027px] text-center mt-4">
                  You haven’t added any units yet.
                </p>
                <p className="text-dark-grey dark:text-white text-lg leading-6 font-normal tracking-[0.027px] mt-6 text-center">
                  Let’s explore and connect your first unit to start using the full features.
                </p>
                <button
                  className="bg-theme text-white text-base font-semibold tracking-[-0.32px] leading-5 min-w-36 px-5 mx-auto h-10 rounded-lg mt-6 flex items-center justify-center hover:bg-theme/80 transition-all duration-300 ease-in-out"
                  onClick={handleNavigateToUnits}
                  data-testid="units-goto-manage-units-button"
                >
                  Go to Assets - Manage Units
                </button>
              </div>
            </div>
          ) : (
            <>

              <div className="titleAndFilters" data-testid="units-title-and-filters">
                <div className="flex flex-col gap-3 sm:flex-row sm:justify-between items-start sm:items-center flex-wrap md:flex-nowrap">
                  <h2 className="mainTitle w-full text-[#000000DE] dark:text-white font-semibold text-xl leading-5 lg:text-2xl lg:leading-6 tracking-[0.036px]" data-testid="units-title">
                    Units {totalCount > 0 && (
                    <span>({totalCount})</span> )}
                  </h2>
                  <div className="searchAndFilter flex items-center gap-4  w-full justify-between md:justify-end">
                    <SearchComponent
                      searchUnitText={searchUnitText}
                      setSearchUnitText= {(val) => updateQuery({ search: val, page: FIRST_PAGE })}
                      setPageSize={setPageSize}
                      trackingContext={SearchContext.units}
                    />
                    <div className="flex items-center gap-3">
                      <SortByDropdown
                        data-testid="units-sort-by-dropdown"
                        orderOptions={ORDER_OPTIONS}
                        sortByOptions={ZONE_UNIT_SORT_OPTIONS}
                        selectedOrder={sortDirection}
                        selectedSortBy={sortBy}
                        onReset={handleSortReset}
                        onApply={handleSortApply}
                      />

                      <FilterDropDown
                        data-testid="units-filter-dropdown"
                        sections={ZONE_UNIT_FILTER_SECTIONS}
                        values={filters}
                        onApply={applyFilters}
                        onClearAll={resetFilters}
                      />



                      <div className="hidden lg:block">
                        <GridListViewComponent data-testid="units-grid-list-view-toggle" gridView={gridView} setGridView={setGridView} />
                      </div>

                      <Button
                        type="button"
                        onClick={handleDownloadUnitsCsv}
                        disabled={isDownloadingUnitsCsv || totalCount === 0}
                        data-testid="download-units-csv-button"
                        title="Download CSV"
                        aria-label="Download units list as CSV"
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md border transition-colors ${
                          isDownloadingUnitsCsv || totalCount === 0
                            ? "cursor-not-allowed border-[#E1E5EA] text-[#BDBDBD] dark:border-darkThemeBorder dark:text-white/30"
                            : "border-[#E1E5EA] text-theme hover:bg-[#FFF4EF] dark:border-darkThemeBorder dark:text-[#FF8C65]"
                        }`}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M11.6667 1.66699H5.83333C4.91286 1.66699 4.16667 2.41318 4.16667 3.33366V16.667C4.16667 17.5875 4.91286 18.3337 5.83333 18.3337H14.1667C15.0871 18.3337 15.8333 17.5875 15.8333 16.667V5.83366L11.6667 1.66699Z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M11.6667 1.66699V5.83366H15.8333"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M10 8.33301V13.333"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                          <path
                            d="M8.125 11.458L10 13.333L11.875 11.458"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M7.5 15H12.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </Button>
                    </div>
                  </div>
                </div>
                {!gridView && bulkActionHeader}
              </div>


             <div className="mt-4 unitsHolder">
            {isInitialUnitsLoading ? null : isEmptyUnitsResult ? (
              <p className="text-center text-dark-gray dark:text-white mt-4" data-testid="units-no-results">
                {isFiltersApplied ? "No units found with the applied filters." : "No units match your search."}
              </p>
            ) : (
              <>
                {!unitsFetching && gridView && (
                  <div className="grid max-[430px]:grid-cols-1 grid-cols-[repeat(auto-fill,minmax(312px,1fr))] gap-6">
                    {unitsByFleetUnId?.map((unit) => (
                      <ViewUnitCardComponent
                        key={unit?.serialNumber}
                        info={unit}
                        setSelectedUnits={setSelectedUnit}
                        selectedUnit={selectedUnit}
                        handleEditUnit={handleEditUnit}
                        handleRemoveUnit={handleRemoveUnit}
                        handleGenerateReport={handleGenerateReport}
                        activeDropdownId={activeDropdownId}
                        setActiveDropdownId={setActiveDropdownId}
                      />
                    ))}
                  </div>
                )}
                {!unitsFetching && !gridView && (
                  <UnitsListViewComponent
                    units={unitsByFleetUnId}
                    fleet={selectedFleet}
                    activeDropdownId={activeDropdownId}
                    setActiveDropdownId={setActiveDropdownId}
                    handleEditUnit={handleEditUnit}
                    handleRemoveUnit={handleRemoveUnit}
                    handleGenerateReport={handleGenerateReport}
                    setSelectedUnits={setSelectedUnit}
                    selectedUnitsForBulkAction={selectedUnitsForBulkAction}
                    addUnitsToSelectedUnitsForBulkAction={addUnitsToSelectedUnitsForBulkAction}
                    removeUnitsFromSelectedUnitsForBulkAction={removeUnitsFromSelectedUnitsForBulkAction}
                    selectedSortBy={sortBy}
                    selectedOrder={sortDirection}
                    onSortChange={handleSortApply}
                  />
                )}
                {hasMoreUnits && (
                  <div ref={loaderRef} className="w-full h-1" />
                )}
              </>
            )}
          </div>
            </>
          )}

          {totalCount > DEFAULT_PAGE_SIZE && (
            <Pagination pageCount={pageCount} onPageChange={handlePageClick} currentPage={currentPage}/>
          )}

           {isGenerateReportModalOpen && (
        <GenerateReportModal
          isOpen={isGenerateReportModalOpen}
          onClose={() => {
            setIsGenerateReportModalOpen(false);
            setReportPrefillData(null);
          }}
          selectedFleet={selectedFleet}
          allPodData={allPodData}
          showToast={showToast}
          prefillData={reportPrefillData}
        />
      )}

          {showDeleteModel && (
            <ConfirmModal
              title="Remove Unit from Zone"
              subText="Your unit will be unassigned."
              confirmMessage="Are you sure you want to remove this unit from zone?"
              cancelText="Cancel"
              submitText="Confirm"
              onCancel={handleCancel}
              onSubmit={makeHandleRemoveConfirmCallback(removeUnitsFromSelectedUnitsForBulkAction)}
            />
          )}

          <ToastMessage
            message={toast.message}
            type={toast.type}
            show={toast.show}
            onClose={() => setToast((prev) => ({ ...prev, show: false }))}
          />
        </>
      }
    </BulkUnitSelectionContainer>
  );
}
